import PullToRefresh from "@dockethealth/pulltorefreshjs";
import { vibrateDevice } from "./capacitor";

function computePiecewiseLinear(
  x: number,
  segments: { startingAtX: number; denominator: number }[]
): number {
  let ret = 0;

  for (let i = 0; i < segments.length; i++) {
    const { startingAtX, denominator } = segments[i];
    const nextStartingAtX = segments[i + 1]?.startingAtX ?? Infinity;

    if (x <= startingAtX) {
      break;
    }
    const segmentEnd = Math.min(x, nextStartingAtX);
    const segmentContribution = (segmentEnd - startingAtX) / denominator;

    ret += segmentContribution;
  }

  return ret;
}

export function initPullToRefresh(params: {
  pullToRefreshMainElement: HTMLElement;
  onRefresh: PullToRefresh.Options["onRefresh"];
  shouldPullToRefresh: PullToRefresh.Options["shouldPullToRefresh"];
}) {
  return PullToRefresh.init({
    mainElement: `#${params.pullToRefreshMainElement.id}`,
    triggerElement: "body",
    /**
     * The annoying thing is that the library uses refreshTimeout to
     *
     * A) Delay the invocation of onRefresh
     * B) In a timeout used to remove the PTR refresh element from the DOM.
     *
     * If you set it to 0, then the animation to snap the PTR back to the top of the screen won't play.
     * If you set it too high, then you can run into an issue when PTR'ing multiple times in a row where the element
     * removal doesn't happen until AFTER you've started your next drag. We set it to 300ms to line it up with the .3s
     * transition.
     */
    refreshTimeout: 300,
    distThreshold: 80,
    onReleasingWhilePulling: () => {
      vibrateDevice();
    },
    /**
     * Could probably use a hill function here instead, but using a piecewise linear function over such a small distance
     * feels to the user like a fairly smooth increase in resistance.
     * https://www.desmos.com/calculator/ey3mljtzxg
     */
    distanceResistedFunction: ({ distExtra }) => {
      const segments = [
        { startingAtX: 0, denominator: 1 },
        { startingAtX: 10, denominator: 1.25 },
        { startingAtX: 25, denominator: 1.5 },
        { startingAtX: 50, denominator: 2 },
        { startingAtX: 75, denominator: 3 },
        { startingAtX: 120, denominator: 5 },
      ];
      const ret = computePiecewiseLinear(distExtra, segments);
      return ret;
    },
    /**
     * Copying the exact default from https://github.com/BoxFactura/pulltorefresh.js/blob/master/src/lib/markup.js and
     * then applying tailwind styles on top.
     */
    getMarkup: () => {
      return `
<div class="__PREFIX__box">
  <div class="__PREFIX__content">
    <div class="__PREFIX__icon !text-base !text-white"></div>
    <div class="__PREFIX__text !text-base !text-white"></div>
  </div>
</div>
`;
    },
    // TODO: Resistance function w/threshold and distmax that simulates an elastic pull better
    shouldPullToRefresh: params.shouldPullToRefresh,
    onRefresh: params.onRefresh,
  });
}
